// MARK://dhk8/admin-shell/lib/clients/audit
// Auditoría → subgraph platform-audit (compuesto en el gateway). Lectura POR EL GRAFO
// (tenant del JWT, RLS). Dos vistas:
//   · listAuditLog(entityId, …)  → historial de UNA entidad (aggregateId = id).
//   · listAuditFeed(filter, …)   → feed global del tenant, filtrable.
// `payload` viene como String JSON crudo del grafo; lo conservamos tal cual y lo parsea
// la UI (audit-format.ts). El tenantId NO se manda — lo pone el JWT (ADR-0003).
import { requireTenant } from '../api.js'
import { PREVIEW_STUB, mock } from '../env.js'
import { gqlClient } from '../gql.js'
import { AdminAuditLogQuery, AdminAuditFeedQuery } from '../queries/audit.queries.js'

/** Una entrada de auditoría (vista de UI). `payload` es JSON crudo (string). */
export interface AuditEntry {
  id: string
  eventName: string
  aggregateId: string | null
  actorType: string
  actorId: string | null
  severity: string
  timestamp: string
  payload: string
}

/** Página de entradas (paginación server-side). */
export interface AuditLogPage {
  items: AuditEntry[]
  total: number
  page: number
  limit: number
}

/** Filtro del feed global (auditFeed). Campos opcionales; vacío = sin filtro. */
export interface AuditFeedFilter {
  module?: string
  eventName?: string
  actorId?: string
  severity?: string
  from?: string
  to?: string
}

// Mapea el AuditEntry del grafo (typed-document-node) al tipo de UI.
interface GqlAuditEntry {
  id: string
  eventName: string
  aggregateId?: string | null
  actorType: string
  actorId?: string | null
  severity: string
  timestamp: string
  payload: string
}
function toUiEntry(e: GqlAuditEntry): AuditEntry {
  return {
    id: e.id,
    eventName: e.eventName,
    aggregateId: e.aggregateId ?? null,
    actorType: e.actorType,
    actorId: e.actorId ?? null,
    severity: e.severity,
    timestamp: e.timestamp,
    payload: e.payload,
  }
}

interface GqlAuditPage {
  items: ReadonlyArray<GqlAuditEntry>
  total: number
  page: number
  limit: number
}
function toUiPage(p: GqlAuditPage): AuditLogPage {
  return { items: p.items.map(toUiEntry), total: p.total, page: p.page, limit: p.limit }
}

const EMPTY = (page: number, limit: number): AuditLogPage => ({ items: [], total: 0, page, limit })

// ── preview store ────────────────────────────────────────────────────────────
// Un par de entradas realistas para que las pantallas de preview no salgan vacías.
function previewEntries(): AuditEntry[] {
  const now = Date.now()
  return [
    {
      id: 'aud-demo-1', eventName: 'dhk8.erp.warehouse.updated', aggregateId: 'wh-demo-1',
      actorType: 'user', actorId: 'usr_a1b2c3d4e5', severity: 'low',
      timestamp: new Date(now - 1000 * 60 * 5).toISOString(),
      payload: JSON.stringify({ changes: { name: { before: 'Bodega', after: 'Bodega principal' }, active: { before: false, after: true } } }),
    },
    {
      id: 'aud-demo-2', eventName: 'dhk8.catalog.product.deleted', aggregateId: 'prd-demo-9',
      actorType: 'user', actorId: 'usr_a1b2c3d4e5', severity: 'medium',
      timestamp: new Date(now - 1000 * 60 * 60).toISOString(),
      payload: JSON.stringify({ fields: ['name', 'sku'], name: 'Combo viejo' }),
    },
  ]
}

/** Historial de UNA entidad (aggregateId = id). Paginación server-side. */
export async function listAuditLog(
  entityId: string,
  opts: { eventName?: string; page?: number; limit?: number } = {},
  signal?: AbortSignal,
): Promise<AuditLogPage> {
  const page = opts.page ?? 1
  const limit = opts.limit ?? 20
  if (PREVIEW_STUB) {
    const items = previewEntries().filter((e) => e.aggregateId === entityId)
    return mock({ items, total: items.length, page, limit })
  }
  if (!entityId || requireTenant() === null) return EMPTY(page, limit)
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient
    .query(AdminAuditLogQuery, { entityId, eventName: opts.eventName, page, limit }, ctx)
    .toPromise()
  if (res.error) throw res.error
  return res.data?.auditLog ? toUiPage(res.data.auditLog) : EMPTY(page, limit)
}

/** Feed global del tenant, filtrable. Paginación server-side. */
export async function listAuditFeed(
  filter: AuditFeedFilter,
  opts: { page?: number; limit?: number } = {},
  signal?: AbortSignal,
): Promise<AuditLogPage> {
  const page = opts.page ?? 1
  const limit = opts.limit ?? 20
  if (PREVIEW_STUB) {
    const items = previewEntries()
    return mock({ items, total: items.length, page, limit })
  }
  if (requireTenant() === null) return EMPTY(page, limit)
  // Manda solo las claves con valor (el filtro vacío = sin filtro).
  const gqlFilter: AuditFeedFilter = {}
  if (filter.module) gqlFilter.module = filter.module
  if (filter.eventName) gqlFilter.eventName = filter.eventName
  if (filter.actorId) gqlFilter.actorId = filter.actorId
  if (filter.severity) gqlFilter.severity = filter.severity
  if (filter.from) gqlFilter.from = filter.from
  if (filter.to) gqlFilter.to = filter.to
  const hasFilter = Object.keys(gqlFilter).length > 0
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient
    .query(AdminAuditFeedQuery, { filter: hasFilter ? gqlFilter : undefined, page, limit }, ctx)
    .toPromise()
  if (res.error) throw res.error
  return res.data?.auditFeed ? toUiPage(res.data.auditFeed) : EMPTY(page, limit)
}
